import {
  useSelectedCategorySidebar,
  useSidebar,
} from "../../contexts/SidebarContext";
import { fetchCategories } from "../../helpers/querycalls";
import { sidebar_items } from "../../helpers/constants";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import React, { useState, useRef, useEffect } from "react";

const Sidebar = ({ categoryId }) => {
  const sidebarRef = useRef(null);

  // =======================
  // Custom hooks
  // =======================
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { setSelectedCategorySidebar } = useSelectedCategorySidebar();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        toggleSidebar(); // Close the drawer if you click outside it
      }
    };

    if (sidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside); // Add listener to window when dropdown is open
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside); // Clean up listener if it's closed
    };
  }, [sidebarOpen, toggleSidebar]);

  // =======================
  // useStates
  // =======================
  const [closedIds] = useState([]);
  const [linksOpen] = useState(true);

  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // =======================
  // Partial components
  // =======================
  const CategoryItem = ({ categoryName, emoji, linkhref, id, category }) => {
    const singleCategory =
      categories?.find((category) => category.id === Number(id)) ||
      categories
        ?.flatMap((category) => category.subcategory_list || [])
        .find((sub) => sub.id === Number(id));
    return (
      <div className="flex gap-2 cursor-pointer">
        <Link
          to={linkhref}
          className={`sm:w-[186px] w-full transition-colors my-[2px] pt-1.5 px-4 rounded-lg ${
            Number(categoryId) === id
              ? "bg-[#e5ebec] text-teal-700 font-bold"
              : "hover:bg-[#e5ebec] hover:text-teal-700 hover:font-bold"
          }`}
          onClick={() => {
            setSelectedCategorySidebar({ categoryName, id });
            toggleSidebar();
          }}
        >
          <li className="inline-flex items-center gap-2 mt-0.5 sm:text-[16px] text-[14px] hover:text-teal-700">
            {id === -1 || id === -2 ? (
              <>
                <img
                  loading="eager"
                  src={emoji}
                  className="inline-block max-w-6 max-h-6"
                  alt="emoji"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/images/logo.svg";
                  }}
                />
                <p className="-mb-1.5">{categoryName}</p>
              </>
            ) : singleCategory?.uploaded_logo ? (
              <>
                <img
                  loading="eager"
                  src={
                    process.env.REACT_APP_API_URL +
                    singleCategory?.uploaded_logo.url
                  }
                  className="inline-block max-w-6 max-h-6"
                  alt="emoji"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/images/logo.svg";
                  }}
                />
                <p className="-mb-1.5">{categoryName}</p>
              </>
            ) : (
              <>
                <img
                  src={"/images/logo.svg"}
                  alt="emoji"
                  className="inline-block max-w-6 max-h-6"
                />
                <p className="-mb-1.5">{categoryName}</p>
              </>
            )}
          </li>
        </Link>
      </div>
    );
  };

  const RbatItem = ({ name, image, linkhref }) => {
    return (
      <div className="flex gap-2 cursor-pointer hover:text-teal-700">
        <img src={image} alt="" />
        <Link to={linkhref}>
          <li className="inline sm:text-[16px] text-[14px]  w-full">{name}</li>
        </Link>
      </div>
    );
  };

  return (
    <div className="min-h-[calc(100dvh-90px)]">
      <div
        ref={sidebarRef}
        className={`h-full z-[51] md:z-0 p-4 pt-0 sm:w-[278px] w-[90%] md:pt-[22px] md:block transition-all duration-300 ease-in-out transform ${
          sidebarOpen ? "translate-x-0" : "translate-x-full"
        } overflow-y-auto md:overflow-y-visible md:translate-x-0 fixed top-0 right-0 z-40 md:relative bg-white md:bg-transparent border-l-[2px] border-[#F1F1F1]`}
        dir="rtl"
      >
        <div className="border-l-1 md:sticky md:top-[112px] border-grey pt-[20px] pr-[4px] md:pt-0 md:pr-0">
          <img
            src="/images/sidebar/cross.svg"
            alt="toggle sidebar"
            className="block md:hidden"
            onClick={toggleSidebar}
          />
          <ul className="text-gray-600 text-right mr-5 mt-[30px] md:mt-0">
            <CategoryItem
              categoryName="الرئيسية"
              emoji="/images/sidebar/home-emoji.png"
              id={-1}
              linkhref={`/`}
            />
            <CategoryItem
              categoryName="ابدأ من هنا"
              emoji="/images/sidebar/starter-emoji.png"
              id={-2}
              linkhref={`/starter`}
            />
            {/* <CategoryItem categoryName="أخبار سلة" emoji="/images/sidebar/news-emoji.png" id={-3} linkhref={`/news`} /> */}
          </ul>

          {categories?.map((category) => {
            if (!category.subcategory_list) {
              if (!category.has_children) {
                return (
                  <ul
                    className="text-gray-600 text-right mr-5"
                    key={category.id}
                  >
                    <CategoryItem
                      categoryName={category.name}
                      emoji={category.emoji}
                      key={category.id}
                      id={category.id}
                      category={category}
                      linkhref={`/category-detail/${category.id}`}
                    />
                  </ul>
                );
              }
            } else {
              return (
                <React.Fragment key={category.id}>
                  <div className="flex justify-between items-center w-full mt-3 cursor-pointer">
                    <h3 className="font-medum sm:text-[16px] text-[14px] text-[#999] text-right ms-8 cursor-pointer">
                      {category.name}
                    </h3>
                  </div>
                  {!closedIds.includes(category.id) && (
                    <ul className="text-gray-600 text-right mr-5">
                      {category.subcategory_list.map((subcategory) => (
                        <React.Fragment key={subcategory.id}>
                          <CategoryItem
                            categoryName={subcategory.name}
                            emoji={subcategory.emoji}
                            id={subcategory.id}
                            linkhref={`/category-detail/${subcategory.id}`}
                            subcategory={subcategory}
                          />
                        </React.Fragment>
                      ))}
                    </ul>
                  )}
                </React.Fragment>
              );
            }
            return <></>;
          })}
          <div className="flex justify-between items-center w-48 mt-3 cursor-pointer">
            <h3 className="font-medium sm:text-[16px] text-[14px] text-[#999] text-right mb-4 mt-4 ms-8">
              روابط مهمة
            </h3>
          </div>
          {linksOpen && (
            <ul className="space-y-2 text-gray-600 text-right ms-10">
              {sidebar_items.rawabat_items.map((item) => (
                <React.Fragment key={item.id}>
                  <RbatItem
                    name={item.name}
                    image={item.image}
                    linkhref={item.link}
                  />
                </React.Fragment>
              ))}
            </ul>
          )}

          <div className="flex flex-col mt-8 ms-8 text-[12px] text-[#999]">
            <bdi className="self-start text-medium text-[14px]">©2025</bdi>
            <p className="text-medium">جميع الحقوق محفوظة لسلة</p>
            {/* <div className="flex gap-4">
              <a className="hover:text-[#444]" href="">
                الشروط والاحكام
              </a>
              <a className="hover:text-[#444]" href="">
                الخصوصية
              </a>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
